/**
 * Custom GlobalFilterComponent - Override Component
 */
import { Component, QueryList, ViewChildren, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GlobalFilterComponent as ParentGlobalFilterComponent } from '@shared-lib/global-components/global-filter/global-filter.component';
import { MatSliderComponent } from '@shared-lib/global-components/mat-slider/mat-slider.component';
import { MatIconModule } from '@angular/material/icon';
import { GlobalDropdownComponent } from '../global-dropdown/global-dropdown.component';
import { ButtonComponent } from '@shared-lib/global-components/button/button.component';
import { SearchContainerComponent } from '@shared-lib/global-components/search-container/search-container.component';
import { MultiselectToggle } from '@shared-lib/global-components/multiselect-toggle/multiselect-toggle';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs';

@Component({
  selector: 'app-global-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    GlobalDropdownComponent,
    MatSliderComponent,
    ButtonComponent,
    SearchContainerComponent,
    MultiselectToggle,
  ],
  templateUrl: './global-filter.component.html',
  styleUrls: ['./global-filter.component.scss']
})
export class GlobalFilterComponent extends ParentGlobalFilterComponent {
  private readonly traceTag = '[REP-MAP-DBG]';
  protected filterListener: string | string[] | null = null;
  private readonly nestedFilterKeys = new Set(['region', 'country', 'mine', 'owner_type', 'ownerType']);
  private readonly screen2FilterKeyMap: Record<string, string> = {
    engine_model_key: 'engine_model_key',
    machine_model: 'machine_model',
    repower_score: 'repower_score',
    cmi_actual_tco_dollar_hr: 'cmi_actual_tco_dollar_hr',
  };
  private optionsHistory: Record<string, any[]> = {};
  private isScreen2Context = false;
  private formatScreen2PayloadValue(value: any): any {
    if (Array.isArray(value)) {
      const flattened = value
        .flat(Infinity)
        .filter((item) => item !== null && item !== undefined && item !== '');
      return [flattened];
    }
    return [[value]];
  }
  private normalizeOptionsList(optionsCandidate: any): any[] | null {
    if (!Array.isArray(optionsCandidate)) return null;

    const flattened = optionsCandidate
      .flat(Infinity)
      .map((item) => typeof item === 'string' ? item.trim() : item)
      .filter((item) => item !== null && item !== undefined && item !== '');

    if (flattened.length === 0) {
      return [];
    }

    const uniqueOptions = Array.from(new Set(flattened));
    return uniqueOptions.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    );
  }

  private normalizePayloadForDropdownOptions(payload: any): any {
    const normalizedPayload = JSON.parse(JSON.stringify(payload || {}));

    const ensureNestedFields = (obj: any) => {
      if (!obj || this.isScreen2Context) return; // Do not wrap for Screen 2
      this.nestedFilterKeys.forEach((key) => {
        const value = obj[key];
        if (value === null || value === undefined || value === '') return;
        if (Array.isArray(value)) {
          if (value.length === 0) return;
          if (Array.isArray(value[0])) return;
          obj[key] = [value];
          return;
        }
        obj[key] = [[value]];
      });
    };

    if (normalizedPayload?.requestData?.filter) {
      ensureNestedFields(normalizedPayload.requestData.filter);
    }
    if (normalizedPayload?.requestData) {
      ensureNestedFields(normalizedPayload.requestData);
    }

    return normalizedPayload;
  }

  private resolveLiveFilterData(res: any): Record<string, any> | null {
    const fromResponseList = res?.responseList?.[0]?.['filter-data']?.[0]?.data;
    if (fromResponseList && typeof fromResponseList === 'object') {
      return fromResponseList;
    }

    const fromDirect = res?.['filter-data']?.[0]?.data;
    if (fromDirect && typeof fromDirect === 'object') {
      return fromDirect;
    }

    return null;
  }

  private buildFilterDataFromUiRows(res: any): Record<string, any> | null {
    const rows = res?.responseList?.[0]?.['ui-data']?.[0]?.data || res?.['ui-data']?.[0]?.data;
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const mapped: Record<string, any[]> = {};
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      for (const key of Object.keys(row)) {
        const value = row[key];
        if (value === null || value === undefined || value === '') continue;
        if (!mapped[key]) mapped[key] = [];
        mapped[key].push(value);
      }
    }

    Object.keys(mapped).forEach((k) => {
      mapped[k] = Array.from(new Set(mapped[k]));
    });

    return mapped;
  }

  private mergeCurrentSelectionsIntoDropdownPayload(payload: any): any {
    const mergedPayload = JSON.parse(JSON.stringify(payload || {}));
    if (!mergedPayload.requestData) {
      mergedPayload.requestData = {};
    }
    const shouldUseNestedFilterObject =
      !this.isScreen2Context &&
      mergedPayload.requestData.filter !== undefined;

    if (shouldUseNestedFilterObject) {
      if (!mergedPayload.requestData.filter || typeof mergedPayload.requestData.filter !== 'object') {
        mergedPayload.requestData.filter = {};
      }
    }

    const formValues = this.filterForm?.value || {};
    Object.keys(formValues).forEach((key) => {
      const value = formValues[key];
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value) && value.length === 0) return;
      const target = shouldUseNestedFilterObject
        ? mergedPayload.requestData.filter
        : mergedPayload.requestData;

      if (this.isScreen2Context) {
        target[key] = this.formatScreen2PayloadValue(value);
      } else if (this.nestedFilterKeys.has(key)) {
        target[key] = this.normalizeOptionsList(value) || [];
      } else {
        target[key] = value;
      }
    });

    return this.normalizePayloadForDropdownOptions(mergedPayload);
  }

  override ngOnInit(): void {
    // Store the filterListener from the config
    this.filterListener = this.config?.filterListener || null;
    const currentUrl = ((this as any).router?.url || '').toLowerCase();
    const listenerText = Array.isArray(this.filterListener)
      ? this.filterListener.join(',').toLowerCase()
      : `${this.filterListener || ''}`.toLowerCase();
    this.isScreen2Context =
      currentUrl.includes('repower-screen-2') ||
      listenerText.includes('world-map-2');

    const { apiUrl } = this.config;
    // Filter out items without a valid type
    const validTypes = ['multiselect-toggle', 'dropdown', 'slider-range', 'button', 'icon', 'search'];
    this.filters = [...(this.config?.content || [])].filter((f) => validTypes.includes(f?.type));

    // Add filterListener to each filter item
    this.filters = this.filters.map((filter: any) => ({
      ...filter,
      filterListener: this.filterListener,
    }));

    // Calculate reset button based on filtered items
    this.showResetButton = this.filters.some((f) => f.type !== 'search');

    this.showResetButton = this.filters.some((f) => f.type !== 'search');
    const queryParams = (this as any).route?.snapshot?.queryParams;

    this.filters.forEach((item: any) => {
      if (item?.key) {
        this.filterForm.addControl(item.key, new FormControl(null));
        if (item.options === 'data' && item.optionsList) {
          this.options[item.key] = item.optionsList;
        }
      }
    });

    // Helper method to fetch and map filter options
    const fetchAndMapFilterOptions = (payload: any) => {
      if (apiUrl) {
        const traceId = `DD-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const dropdownPayload = this.mergeCurrentSelectionsIntoDropdownPayload(payload);
        try {
          console.log(`${this.traceTag} ${traceId} dropdownRequest`, {
            widgetId: this.config?.widgetId,
            apiUrl,
            isScreen2Context: this.isScreen2Context,
            requestData: dropdownPayload?.requestData,
            formValue: this.filterForm?.value,
          });
        } catch (_e) {
          // ignore
        }
        (this as any).configService.getDropdownOptions(apiUrl, dropdownPayload).subscribe({
          next: (res: any) => {
          try {
            console.log(`${this.traceTag} ${traceId} dropdownResponseRaw`, res);
          } catch (_e) {
            // ignore
          }
          // Screen-2 mapping source of truth: responseList[0].filter-data[0].data
          // Fallback to ui-data only when filter-data is absent.
          const filterData = this.resolveLiveFilterData(res) || this.buildFilterDataFromUiRows(res) || {};
          try {
            console.log(`${this.traceTag} ${traceId} extractedFilterData`, filterData);
          } catch (_e) {
            // ignore
          }
          if (filterData) {
            this.filters = this.filters.map((filter: any) => {
              if (!filter?.key) return filter;

              const mappedKey = this.isScreen2Context
                ? (this.screen2FilterKeyMap[filter.key] || filter.key)
                : filter.key;
              const optionsCandidate = filterData[mappedKey] ?? res[filter.key];
              let options = this.normalizeOptionsList(optionsCandidate);
              if (this.isScreen2Context && filter.key === 'repower_score' && Array.isArray(options)) {
                options = options.map((v: any) => String(v));
              }
              const hasKeyInFilterData = Object.prototype.hasOwnProperty.call(filterData, mappedKey);
              const hasExplicitEmptyOptions = hasKeyInFilterData && Array.isArray(optionsCandidate) && optionsCandidate.length === 0;
              try {
                console.log(`${this.traceTag} ${traceId} keyOptions`, {
                  uiKey: filter.key,
                  mappedKey,
                  rawCandidate: optionsCandidate,
                  normalizedOptions: options,
                });
              } catch (_e) {
                // ignore
              }
              if (options !== null) {
                const existingOptions = this.options[filter.key] || filter.optionsList || [];
                const historyOptions = this.optionsHistory[filter.key] || [];
                const rememberedOptions = existingOptions.length > 0 ? existingOptions : historyOptions;
                const activeFilterKey = (this as any).globalFilterService.activeFilter;
                const shouldIgnoreTransientEmptyForScreen2 =
                  this.isScreen2Context && hasExplicitEmptyOptions;
                const shouldPreserveCurrentFilterOptions =
                  activeFilterKey === filter.key && rememberedOptions.length > 0;

                // Keep previously seen options for the active dropdown so it doesn't collapse
                // to a single selected value after each API refresh (breaks multi-select UX).
                const mergedRemembered = Array.from(new Set([...(rememberedOptions || []), ...(options || [])]));
                if (mergedRemembered.length > 0) {
                  this.optionsHistory[filter.key] = mergedRemembered;
                }

                const stableOptions = this.isScreen2Context
                  ? (shouldIgnoreTransientEmptyForScreen2
                    ? [...rememberedOptions]
                    : (options !== null ? [...options] : [...rememberedOptions]))
                  : hasExplicitEmptyOptions
                    ? []
                  : (shouldPreserveCurrentFilterOptions
                    ? (historyOptions.length > 0 ? historyOptions : mergedRemembered)
                    : (options.length > 0 ? options : rememberedOptions));
                const control = this.filterForm.get(filter.key);
                const isEmptyValue = control?.value === null || (Array.isArray(control?.value) && control?.value?.length === 0);

                if (hasExplicitEmptyOptions && !shouldIgnoreTransientEmptyForScreen2 && control && !isEmptyValue) {
                  const clearedValue = filter.isMultiSelect ? [] : null;
                  control.setValue(clearedValue, { emitEvent: false });
                  (this as any).globalFilterService.updateFilterSelection(
                    filter.key,
                    clearedValue,
                    filter.isQueryParam || false,
                    undefined,
                    false,
                    this.filterListener || undefined
                  );
                }

                if (this.isScreen2Context && control) {
                  const rawControlValue = control.value;
                  const currentValue = filter.key === 'repower_score'
                    ? (Array.isArray(control.value)
                      ? control.value.map((v: any) => String(v))
                      : (control.value === null || control.value === undefined || control.value === '' ? control.value : String(control.value)))
                    : control.value;
                  if (filter.key === 'repower_score' && JSON.stringify(rawControlValue) !== JSON.stringify(currentValue)) {
                    control.setValue(currentValue, { emitEvent: false });
                  }
                  const allowedValues = new Set((stableOptions || []).map((v: any) => String(v)));
                  let nextValue = currentValue;

                  if (Array.isArray(currentValue)) {
                    nextValue = currentValue.filter((v: any) => allowedValues.has(String(v)));
                  } else if (currentValue !== null && currentValue !== undefined && currentValue !== '' && !allowedValues.has(String(currentValue))) {
                    nextValue = null;
                  }

                  if (JSON.stringify(nextValue) !== JSON.stringify(currentValue)) {
                    control.setValue(nextValue, { emitEvent: false });
                    (this as any).globalFilterService.updateFilterSelection(
                      filter.key,
                      nextValue,
                      filter.isQueryParam || false,
                      undefined,
                      false,
                      this.filterListener || undefined
                    );
                  }
                }

                // Apply dynamic defaults
                if (stableOptions.length > 0 && filter.isfirstOptionDefault && (isEmptyValue || (this as any).autoPatchedKeys.has(filter.key))) {
                  const defaultValue = filter.isMultiSelect ? [stableOptions[0]] : stableOptions[0];
                  control?.setValue(defaultValue, { emitEvent: false });
                  // Pass filterListener when updating filters
                  (this as any).globalFilterService.updateFilterSelection(
                    filter.key,
                    defaultValue,
                    filter.isQueryParam || false,
                    undefined,
                    true,
                    this.filterListener
                  );
                  (this as any).autoPatchedKeys.add(filter.key);
                }

                return { ...filter, optionsList: stableOptions, filterListener: this.filterListener };
              }
              return { ...filter, filterListener: this.filterListener };
            });

            // Update internal options map only with valid arrays, avoid wiping options with null/object values
            this.filters.forEach((filter: any) => {
              const key = filter?.key;
              if (!key) return;
              const mappedKey = this.isScreen2Context
                ? (this.screen2FilterKeyMap[key] || key)
                : key;
              const rawCandidate = filterData[mappedKey] ?? res[key];
              let candidate = this.normalizeOptionsList(rawCandidate);
              if (this.isScreen2Context && key === 'repower_score' && Array.isArray(candidate)) {
                candidate = candidate.map((v: any) => String(v));
              }
              const hasKeyInFilterData = Object.prototype.hasOwnProperty.call(filterData, mappedKey);
              const hasExplicitEmptyOptions = hasKeyInFilterData && Array.isArray(rawCandidate) && rawCandidate.length === 0;
              const activeFilterKey = (this as any).globalFilterService.activeFilter;
              const shouldIgnoreTransientEmptyForScreen2 =
                this.isScreen2Context && hasExplicitEmptyOptions;
              if (hasExplicitEmptyOptions) {
                if (shouldIgnoreTransientEmptyForScreen2) {
                  const history = this.optionsHistory[key] || this.options[key] || [];
                  if (Array.isArray(history) && history.length > 0) {
                    this.options[key] = history;
                  }
                  return;
                }
                this.options[key] = [];
                return;
              }
              if (key === activeFilterKey) {
                if (this.isScreen2Context && Array.isArray(candidate)) {
                  this.optionsHistory[key] = candidate;
                  this.options[key] = candidate;
                  return;
                }
                const history = this.optionsHistory[key] || this.options[key] || [];
                if (Array.isArray(history) && history.length > 0) {
                  this.options[key] = history;
                }
                return;
              }
              if (this.isScreen2Context && Array.isArray(candidate)) {
                this.options[key] = candidate;
                this.optionsHistory[key] = candidate;
                return;
              }
              if (Array.isArray(candidate) && candidate.length > 0) {
                this.options[key] = candidate;
                this.optionsHistory[key] = Array.from(new Set([...(this.optionsHistory[key] || []), ...candidate]));
              }
            });
            this.options = { ...this.options }; // trigger change detection for the template
            (this as any).globalFilterService.dropdownOptions = this.options;
            try {
              console.log(`${this.traceTag} ${traceId} finalOptionsMap`, this.options);
            } catch (_e) {
              // ignore
            }
          } // closes if (filterData)
          }, // closes next
          error: (err: any) => {
            console.error(`${this.traceTag} ${traceId} dropdownError`, err);
          }
        });
      }
    };

    // **Initial API call on page load to populate all filter options (region, country, mine)**
    const initialPayload = (this as any).globalFilterService.buildPayload(this.config, []);
    fetchAndMapFilterOptions(initialPayload);

    // Listen to valueChanges
    let valueChange$ = this.filterForm.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    );

    valueChange$.subscribe((val) => {
      const payload = (this as any).globalFilterService.buildPayload(this.config, (this as any).getWhereCondition());
      fetchAndMapFilterOptions(payload);

      // Emit the current filter selection to update all widgets (including world map)
      try {
        const currentRequestParams = Object.fromEntries(
          Object.entries(this.filterForm.value || {}).filter(([_, value]) => {
            if (value === null || value === undefined || value === '') return false;
            if (Array.isArray(value) && value.length === 0) return false;
            return true;
          })
        );
        const filterPayload = {
          queryParams: {},
          requestParams: currentRequestParams,
          // Global filter changes should not be treated as chart-click events.
          // Keeping widgetId null prevents shouldReload() from skipping reloads.
          widgetId: null,
          eventSource: 'global-filter',
          filterListener: this.filterListener || null,
        };
        (this as any).globalFilterService.updateAllFilters(filterPayload);
      } catch (_e) {
        // ignore emitting errors
      }
    });

    // Handle initial trigger
    if (!Object.keys(queryParams || {})?.length) {
      setTimeout(() => (this as any).globalFilterService.initialTrigger(), 200);
    }
  }

  override resetButton(): any {
    this.filterForm.reset();
    (this as any).autoPatchedKeys.clear();
    this.sliders.forEach((slider) => slider.reset());
    
    const currentFilters = (this as any).globalFilterService.getCurrentFilters();
    currentFilters.requestParams = {};
    currentFilters.queryParams = {};
    currentFilters.widgetId = null;
    (currentFilters as any).eventSource = 'global-filter';
    (this as any).globalFilterService.updateAllFilters(currentFilters);

    setTimeout(() => (this as any).globalFilterService.initialTrigger(), 50);
  }
}
